import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronDown, FiChevronUp, FiExternalLink, FiX } from 'react-icons/fi';

// Category → color (matches existing badge colors)
const CATEGORY_COLORS = {
  product: '#3b82f6',
  funding: '#22c55e',
  partnership: '#a855f7',
  kpi: '#f59e0b',
  team: '#06b6d4',
  legal: '#64748b',
  risk: '#ef4444',
  metric: '#f97316',
  valuation: '#10b981',
  meeting: '#0891b2',
  milestone: '#ea580c',
  other: '#6b7280',
};

/**
 * Visual Timeline — horizontal date axis with company swimlanes.
 *
 * Each company gets a row. Events are positioned by date along the axis
 * and colored by category. Click a dot for details.
 */
function TimelineVisual({ events }) {
  const [selectedCompanies, setSelectedCompanies] = useState(new Set());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [detailExpanded, setDetailExpanded] = useState(false);
  const [detailOverflows, setDetailOverflows] = useState(false);
  const [companySelectorOpen, setCompanySelectorOpen] = useState(false);
  const detailRef = useRef(null);
  const scrollRef = useRef(null);
  const tooltipRef = useRef(null);

  // Only show events that have dates (can't position undated on a timeline)
  const datedEvents = useMemo(
    () => events.filter((e) => e.event_date),
    [events]
  );

  // Get all unique companies sorted by event count
  const allCompanies = useMemo(() => {
    const counts = {};
    datedEvents.forEach((e) => {
      counts[e.company_name] = (counts[e.company_name] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ name, count }));
  }, [datedEvents]);

  // Default: show top 15 if no selection
  const visibleCompanies = useMemo(() => {
    if (selectedCompanies.size > 0) {
      return allCompanies.filter((c) => selectedCompanies.has(c.name));
    }
    return allCompanies.slice(0, 15);
  }, [allCompanies, selectedCompanies]);

  const visibleCompanyNames = new Set(visibleCompanies.map((c) => c.name));

  // Compute date range
  const { minDate, maxDate, months } = useMemo(() => {
    const dates = datedEvents
      .filter((e) => visibleCompanyNames.has(e.company_name))
      .map((e) => new Date(e.event_date + 'T00:00:00'));

    if (dates.length === 0) {
      return { minDate: new Date(), maxDate: new Date(), months: [] };
    }

    const min = new Date(Math.min(...dates));
    const max = new Date(Math.max(...dates));

    // Pad by 2 weeks on each side
    min.setDate(min.getDate() - 14);
    max.setDate(max.getDate() + 14);

    // Generate month markers
    const m = [];
    const cursor = new Date(min.getFullYear(), min.getMonth(), 1);
    while (cursor <= max) {
      m.push(new Date(cursor));
      cursor.setMonth(cursor.getMonth() + 1);
    }

    return { minDate: min, maxDate: max, months: m };
  }, [datedEvents, visibleCompanyNames]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalDays = Math.max(1, (maxDate - minDate) / (1000 * 60 * 60 * 24));
  const DAY_WIDTH = 4; // px per day
  const timelineWidth = Math.max(totalDays * DAY_WIDTH, 800);
  const ROW_HEIGHT = 44;
  const LABEL_WIDTH = 160;

  const getX = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    const dayOffset = (d - minDate) / (1000 * 60 * 60 * 24);
    return (dayOffset / totalDays) * timelineWidth;
  };

  const toggleCompany = (name) => {
    setSelectedCompanies((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedCompanies(new Set(allCompanies.map((c) => c.name)));
  };

  const clearSelection = () => {
    setSelectedCompanies(new Set());
  };

  // Check if detail text overflows (is clamped)
  useEffect(() => {
    if (detailRef.current && !detailExpanded) {
      const el = detailRef.current;
      setDetailOverflows(el.scrollHeight > el.clientHeight + 2);
    } else {
      setDetailOverflows(false);
    }
  }, [selectedEvent, detailExpanded]);

  // Close tooltip on outside click
  useEffect(() => {
    const handler = (e) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
        setSelectedEvent(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Get active categories from visible events
  const activeCategories = useMemo(() => {
    const cats = new Set();
    datedEvents
      .filter((e) => visibleCompanyNames.has(e.company_name))
      .forEach((e) => cats.add(e.category));
    return [...cats].sort();
  }, [datedEvents, visibleCompanyNames]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Build company → affinity URL lookup from events
  const affinityUrls = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      if (e.affinity_url && !map[e.company_name]) {
        map[e.company_name] = e.affinity_url;
      }
    });
    return map;
  }, [events]);

  const undatedCount = events.length - datedEvents.length;

  return (
    <div className="tv-container">
      {/* Controls row */}
      <div className="tv-controls">
        {/* Company selector */}
        <div className="tv-company-selector">
          <button
            className="tv-company-toggle"
            onClick={() => setCompanySelectorOpen(!companySelectorOpen)}
          >
            {selectedCompanies.size > 0
              ? `${selectedCompanies.size} companies selected`
              : `Top ${visibleCompanies.length} companies`}
            {companySelectorOpen ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
          </button>
          {companySelectorOpen && (
            <div className="tv-company-dropdown">
              <div className="tv-company-dropdown-actions">
                <button onClick={selectAll}>Select All</button>
                <button onClick={clearSelection}>Clear</button>
              </div>
              <div className="tv-company-list">
                {allCompanies.map((c) => (
                  <label key={c.name} className="tv-company-item">
                    <input
                      type="checkbox"
                      checked={
                        selectedCompanies.size > 0
                          ? selectedCompanies.has(c.name)
                          : allCompanies.indexOf(c) < 15
                      }
                      onChange={() => toggleCompany(c.name)}
                    />
                    <span className="tv-company-name">{c.name}</span>
                    <span className="tv-company-count">{c.count}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Category legend (colors) */}
        <div className="tv-legend">
          {activeCategories.map((cat) => (
            <span key={cat} className="tv-legend-item">
              <span
                className="tv-legend-dot"
                style={{ background: CATEGORY_COLORS[cat] || '#6b7280' }}
              />
              {cat}
            </span>
          ))}
        </div>

        {/* Shape legend (source/type) */}
        <div className="tv-legend tv-legend-shapes">
          <span className="tv-legend-item"><span className="tv-legend-shape shape-square" /> Public</span>
          <span className="tv-legend-item"><span className="tv-legend-shape shape-circle" /> Internal</span>
          <span className="tv-legend-item"><span className="tv-legend-shape shape-diamond" /> Milestone</span>
          <span className="tv-legend-item"><span className="tv-legend-shape shape-triangle" /> Meeting</span>
        </div>

        {undatedCount > 0 && (
          <span className="tv-undated-note">{undatedCount} undated events not shown</span>
        )}
      </div>

      {/* Timeline chart */}
      <div className="tv-chart-wrapper">
        {/* Company labels (fixed left column) */}
        <div className="tv-labels" style={{ width: LABEL_WIDTH }}>
          <div className="tv-labels-header" style={{ height: 36 }} />
          {visibleCompanies.map((c) => (
            <div key={c.name} className="tv-label-row" style={{ height: ROW_HEIGHT }}>
              <Link
                to={`/timeline/${encodeURIComponent(c.name)}`}
                className="tv-label-link"
                title={c.name}
              >
                {c.name.length > 16 ? c.name.substring(0, 14) + '...' : c.name}
              </Link>
              {affinityUrls[c.name] && (
                <a
                  href={affinityUrls[c.name]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tv-label-affinity"
                  title="View in Affinity"
                >
                  <FiExternalLink size={10} />
                </a>
              )}
            </div>
          ))}
        </div>

        {/* Scrollable timeline area */}
        <div className="tv-scroll-area" ref={scrollRef}>
          <div className="tv-timeline" style={{ width: timelineWidth, minHeight: (visibleCompanies.length * ROW_HEIGHT) + 36 }}>
            {/* Month headers */}
            <div className="tv-month-headers" style={{ height: 36 }}>
              {months.map((m, i) => {
                const x = getX(m.toISOString().substring(0, 10));
                return (
                  <div
                    key={i}
                    className="tv-month-marker"
                    style={{ left: x }}
                  >
                    {m.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })}
                  </div>
                );
              })}
            </div>

            {/* Grid lines (one per month) */}
            {months.map((m, i) => {
              const x = getX(m.toISOString().substring(0, 10));
              return (
                <div
                  key={i}
                  className="tv-grid-line"
                  style={{ left: x, top: 36, bottom: 0 }}
                />
              );
            })}

            {/* Company swimlanes */}
            {visibleCompanies.map((c, rowIndex) => (
              <div
                key={c.name}
                className={`tv-swimlane ${rowIndex % 2 === 0 ? 'even' : 'odd'}`}
                style={{ top: 36 + rowIndex * ROW_HEIGHT, height: ROW_HEIGHT }}
              />
            ))}

            {/* Event markers (shape = source, color = category) */}
            {datedEvents
              .filter((e) => visibleCompanyNames.has(e.company_name))
              .map((event) => {
                const x = getX(event.event_date);
                const rowIndex = visibleCompanies.findIndex(
                  (c) => c.name === event.company_name
                );
                if (rowIndex === -1) return null;
                const y = 36 + rowIndex * ROW_HEIGHT + ROW_HEIGHT / 2;
                const color = CATEGORY_COLORS[event.category] || '#6b7280';
                const isHigh = event.significance === 'high';
                const isSelected = selectedEvent?.id === event.id;
                const size = isHigh ? 14 : 10;
                // Shape class: public=square, internal+intelligence=circle, milestone=diamond, meeting=triangle
                const shapeClass =
                  event.source === 'public' ? 'shape-square' :
                  event.event_type === 'milestone' ? 'shape-diamond' :
                  event.event_type === 'meeting' ? 'shape-triangle' :
                  'shape-circle';

                return (
                  <div
                    key={event.id}
                    className={`tv-dot ${shapeClass} ${isHigh ? 'high' : ''} ${isSelected ? 'selected' : ''}`}
                    style={{
                      left: x - size / 2,
                      top: y - size / 2,
                      width: size,
                      height: size,
                      '--dot-color': color,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedEvent(isSelected ? null : event);
                      setDetailExpanded(false);
                    }}
                    title={`${event.company_name}: ${event.headline}`}
                  />
                );
              })}

            {/* Event detail tooltip */}
            {selectedEvent && (() => {
              const x = getX(selectedEvent.event_date);
              const rowIndex = visibleCompanies.findIndex(
                (c) => c.name === selectedEvent.company_name
              );
              const chartHeight = 36 + visibleCompanies.length * ROW_HEIGHT;
              const dotY = 36 + rowIndex * ROW_HEIGHT + ROW_HEIGHT / 2;
              const flipUp = rowIndex >= visibleCompanies.length - 3;
              const color = CATEGORY_COLORS[selectedEvent.category] || '#6b7280';

              return (
                <div
                  ref={tooltipRef}
                  className={`tv-tooltip ${flipUp ? 'flip-up' : ''}`}
                  style={{
                    left: Math.max(8, Math.min(x - 150, timelineWidth - 320)),
                    ...(flipUp
                      ? { bottom: chartHeight - dotY + 12 }
                      : { top: dotY + ROW_HEIGHT / 2 + 8 }),
                  }}
                >
                  <button
                    className="tv-tooltip-close"
                    onClick={() => setSelectedEvent(null)}
                  >
                    <FiX size={14} />
                  </button>
                  <div className="tv-tooltip-header">
                    <span className="tv-tooltip-date">{formatDate(selectedEvent.event_date)}</span>
                    <span
                      className="tv-tooltip-category"
                      style={{ background: color + '22', color }}
                    >
                      {selectedEvent.category}
                    </span>
                  </div>
                  <p className="tv-tooltip-headline">{selectedEvent.headline}</p>
                  {selectedEvent.detail && (
                    <div className={`tv-tooltip-detail ${detailExpanded ? 'expanded' : ''}`}>
                      <div ref={detailRef} className="tv-tooltip-detail-text">
                        <p>{selectedEvent.detail}</p>
                        {selectedEvent.key_data && (
                          <p style={{ fontWeight: 500, marginTop: 4 }}>{selectedEvent.key_data}</p>
                        )}
                      </div>
                      {(detailOverflows || detailExpanded) && (
                        <button
                          className="tv-tooltip-toggle"
                          onClick={(e) => { e.stopPropagation(); setDetailExpanded(!detailExpanded); }}
                        >
                          {detailExpanded ? 'Show less' : 'Show more'}
                        </button>
                      )}
                    </div>
                  )}
                  <div className="tv-tooltip-footer">
                    <Link
                      to={`/timeline/${encodeURIComponent(selectedEvent.company_name)}`}
                      className="tv-tooltip-link"
                    >
                      {selectedEvent.company_name}
                    </Link>
                    {selectedEvent.source_url && (
                      <a
                        href={selectedEvent.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tv-tooltip-link"
                      >
                        <FiExternalLink size={12} /> Source
                      </a>
                    )}
                    {selectedEvent.affinity_url && (
                      <a
                        href={selectedEvent.affinity_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tv-tooltip-link"
                      >
                        Affinity
                      </a>
                    )}
                    <span className="tv-tooltip-source">
                      {selectedEvent.source}{selectedEvent.event_type && selectedEvent.event_type !== 'intelligence' ? ` · ${selectedEvent.event_type}` : ''}
                    </span>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TimelineVisual;
